import React, { useEffect, useState, useCallback } from "react";
import { Loader2, CalendarPlus, MapPin, Video, Users, X, Search, Clock, ImagePlus } from "lucide-react";
import { toast } from "sonner";
import api, { errMsg, fileToBase64 } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const EVENT_TYPES = ["meeting", "social", "anniversary", "workshop", "service", "other"];
const TYPE_COLOR = {
  meeting: "bg-primary/15 text-primary", social: "bg-seafoam/15 text-seafoam",
  anniversary: "bg-brass/20 text-brass", workshop: "bg-warning/15 text-warning",
  service: "bg-primary/15 text-primary", other: "bg-secondary text-muted-foreground",
};

const fmtEvent = (iso) => {
  if (!iso) return "";
  try { return new Date(iso).toLocaleString(undefined, { weekday: "short", month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }); }
  catch { return iso; }
};

const RSVP_OPTS = [{ k: "going", label: "Going" }, { k: "interested", label: "Interested" }, { k: "no", label: "Can't go" }];

const EventCard = ({ ev, mine, onDelete }) => {
  const [rsvp, setRsvp] = useState(ev.my_rsvp || null);
  const [counts, setCounts] = useState(ev.rsvp_counts || { going: 0, interested: 0, no: 0 });

  const setStatus = async (status) => {
    const prev = rsvp;
    setRsvp(status);
    try { const { data } = await api.post(`/community-v2/events/${ev.id}/rsvp`, { status }); if (data.counts) setCounts(data.counts); setRsvp(data.my_rsvp); }
    catch (e) { setRsvp(prev); toast.error(errMsg(e, "Could not RSVP")); }
  };

  return (
    <div data-testid="event-card" className="bg-card border border-border rounded-2xl overflow-hidden shadow-sm animate-fade-up">
      {ev.photo_base64 && <img src={ev.photo_base64} alt={ev.title} className="w-full h-40 object-cover" />}
      <div className="p-5">
        <div className="flex items-center justify-between gap-2 mb-2">
          <span className={`text-xs px-2.5 py-1 rounded-full font-medium capitalize ${TYPE_COLOR[ev.event_type] || TYPE_COLOR.other}`}>{ev.event_type}</span>
          {mine && <button data-testid={`delete-event-${ev.id}`} onClick={() => onDelete(ev.id)} className="text-xs text-muted-foreground hover:text-destructive">Delete</button>}
        </div>
        <h3 className="font-heading text-lg font-semibold">{ev.title}</h3>
        <div className="mt-2 space-y-1 text-sm text-muted-foreground">
          <p className="flex items-center gap-2"><Clock className="w-4 h-4 shrink-0" /> {fmtEvent(ev.starts_at)}</p>
          {ev.is_online ? (
            <p className="flex items-center gap-2"><Video className="w-4 h-4 shrink-0" /> Online{ev.online_link ? <a href={ev.online_link} target="_blank" rel="noreferrer" className="text-primary hover:underline">· link</a> : ""}</p>
          ) : ev.location ? (
            <p className="flex items-center gap-2"><MapPin className="w-4 h-4 shrink-0" /> {ev.location}</p>
          ) : null}
        </div>
        {ev.description && <p className="text-sm text-foreground/80 mt-3 leading-relaxed line-clamp-3">{ev.description}</p>}
        <div className="mt-4 flex items-center justify-between gap-2">
          <div className="flex gap-1.5">
            {RSVP_OPTS.map(({ k, label }) => (
              <button key={k} data-testid={`rsvp-${k}-${ev.id}`} onClick={() => setStatus(k)}
                className={`text-xs font-medium px-3 py-1.5 rounded-full transition-colors ${rsvp === k ? "bg-primary text-primary-foreground" : "border border-border hover:bg-secondary"}`}>
                {label}
              </button>
            ))}
          </div>
          <span className="text-xs text-muted-foreground flex items-center gap-1 shrink-0"><Users className="w-3.5 h-3.5" /> {counts.going || 0}</span>
        </div>
      </div>
    </div>
  );
};

const CreateEventDialog = ({ onClose, onCreated }) => {
  const [form, setForm] = useState({ title: "", event_type: "social", starts_at: "", location: "", is_online: false, online_link: "", description: "", max_attendees: "", photo_base64: "" });
  const [busy, setBusy] = useState(false);
  const up = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value }));

  const pickPhoto = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    if (f.size > 4 * 1024 * 1024) { toast.error("Image too large (max 4MB)"); return; }
    try { const b64 = await fileToBase64(f); setForm((s) => ({ ...s, photo_base64: b64 })); } catch { toast.error("Could not read image"); }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.title.trim() || !form.starts_at) return;
    setBusy(true);
    try {
      await api.post("/community-v2/events", {
        title: form.title.trim(), event_type: form.event_type,
        starts_at: new Date(form.starts_at).toISOString(),
        description: form.description.trim() || null,
        location: form.is_online ? null : (form.location.trim() || null),
        is_online: form.is_online, online_link: form.is_online ? (form.online_link.trim() || null) : null,
        max_attendees: form.max_attendees ? parseInt(form.max_attendees, 10) : null,
        photo_base64: form.photo_base64 || null,
      });
      toast.success("Event created"); onCreated(); onClose();
    } catch (err) { toast.error(errMsg(err, "Could not create event")); }
    finally { setBusy(false); }
  };

  const inp = "w-full px-3 py-2.5 rounded-lg border border-input bg-secondary/40 outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary text-sm";
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-nightsea/75 backdrop-blur-sm" onClick={onClose} />
      <form onSubmit={submit} className="relative w-full max-w-md max-h-[88vh] overflow-y-auto bg-card border border-border rounded-2xl p-6 animate-fade-up">
        <button type="button" onClick={onClose} className="absolute top-4 right-4 text-muted-foreground"><X className="w-5 h-5" /></button>
        <h3 className="font-heading text-xl font-semibold mb-4">Create an event</h3>
        <div className="space-y-3">
          <input data-testid="event-title" className={inp} placeholder="Event title" value={form.title} onChange={up("title")} />
          <div className="grid grid-cols-2 gap-3">
            <select data-testid="event-type" className={inp + " capitalize"} value={form.event_type} onChange={up("event_type")}>
              {EVENT_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
            <input data-testid="event-starts" type="datetime-local" className={inp} value={form.starts_at} onChange={up("starts_at")} />
          </div>
          <label className="flex items-center gap-2 text-sm text-muted-foreground">
            <input type="checkbox" checked={form.is_online} onChange={up("is_online")} className="w-4 h-4 accent-primary" /> This is an online event
          </label>
          {form.is_online
            ? <input data-testid="event-link" className={inp} placeholder="Online link (Zoom, etc.)" value={form.online_link} onChange={up("online_link")} />
            : <input data-testid="event-location" className={inp} placeholder="Location (city, venue)" value={form.location} onChange={up("location")} />}
          <textarea data-testid="event-description" className={inp + " resize-none"} rows={3} placeholder="Description" value={form.description} onChange={up("description")} />
          <div className="flex items-center gap-3">
            <input data-testid="event-max" type="number" className={inp} placeholder="Max attendees (optional)" value={form.max_attendees} onChange={up("max_attendees")} />
            <label className="flex items-center gap-2 text-sm text-muted-foreground cursor-pointer whitespace-nowrap">
              <ImagePlus className="w-4 h-4" /> {form.photo_base64 ? "Photo ✓" : "Photo"}
              <input type="file" accept="image/*" onChange={pickPhoto} className="hidden" />
            </label>
          </div>
        </div>
        <button data-testid="event-create-submit" type="submit" disabled={busy || !form.title.trim() || !form.starts_at}
          className="mt-5 w-full flex items-center justify-center gap-2 bg-primary text-primary-foreground font-semibold py-2.5 rounded-full disabled:opacity-50">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <CalendarPlus className="w-4 h-4" />} Create event
        </button>
      </form>
    </div>
  );
};

const Events = () => {
  const { user } = useAuth();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [show, setShow] = useState(false);
  const [q, setQ] = useState("");
  const [type, setType] = useState("all");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/community-v2/events?upcoming_only=false&limit=100");
      const list = (data.events || []).slice().sort((a, b) => new Date(a.starts_at) - new Date(b.starts_at));
      setEvents(list);
    } catch (e) { toast.error("Could not load events"); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const del = async (id) => {
    if (!window.confirm("Delete this event?")) return;
    try { await api.delete(`/community-v2/events/${id}`); toast.success("Event deleted"); load(); }
    catch (e) { toast.error(errMsg(e, "Could not delete")); }
  };

  const filtered = events.filter((e) =>
    (type === "all" || e.event_type === type) &&
    (!q || (e.title + " " + (e.location || "")).toLowerCase().includes(q.toLowerCase()))
  );

  return (
    <div className="px-4 sm:px-6 py-8">
      <div className="max-w-3xl mx-auto">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="font-heading text-3xl sm:text-4xl font-semibold flex items-center gap-2"><CalendarPlus className="w-7 h-7 text-primary" /> Events</h1>
            <p className="text-muted-foreground mt-2">Find local gatherings or host your own.</p>
          </div>
          <button data-testid="create-event-button" onClick={() => setShow(true)}
            className="flex items-center gap-2 text-sm font-medium bg-primary text-primary-foreground rounded-full px-4 py-2 hover:bg-atlantic transition-colors">
            <CalendarPlus className="w-4 h-4" /> New
          </button>
        </div>

        <div className="flex flex-col sm:flex-row gap-3 mb-6">
          <div className="relative flex-1">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <input data-testid="event-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by title or city…"
              className="w-full pl-9 pr-4 py-2.5 rounded-lg border border-input bg-secondary/40 outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary" />
          </div>
          <select data-testid="event-type-filter" value={type} onChange={(e) => setType(e.target.value)}
            className="px-3 py-2.5 rounded-lg border border-input bg-secondary/40 outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary capitalize">
            <option value="all">All types</option>
            {EVENT_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
          </select>
        </div>

        {loading ? (
          <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-muted-foreground" /></div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-12 text-muted-foreground">
            <CalendarPlus className="w-8 h-8 mx-auto mb-3 opacity-40" />
            <p>No events yet. Be the first to host one for the community.</p>
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 gap-5" data-testid="events-list">
            {filtered.map((e) => <EventCard key={e.id} ev={e} mine={e.created_by === user?.id} onDelete={del} />)}
          </div>
        )}
      </div>
      {show && <CreateEventDialog onClose={() => setShow(false)} onCreated={load} />}
    </div>
  );
};

export default Events;
